import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography
} from '@mui/material'
import { Close } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { loadUserFromStorage } from '../../utils/tokenUtils'

interface LoginDialogProps {
  open: boolean
  onClose: () => void
  redirectTo?: string
  onLoginSuccess?: () => void
}

const LoginDialog: React.FC<LoginDialogProps> = ({ open, onClose, redirectTo, onLoginSuccess }) => {
  const navigate = useNavigate()
  const { login, isLoading } = useAuth()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')

  const handleClose = () => {
    setUsername('')
    setPassword('')
    setError('')
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    try {
      await login({ username, password })
      handleClose()
      if (onLoginSuccess) {
        onLoginSuccess()
        return
      }

      if (redirectTo) {
        navigate(redirectTo, { replace: true })
        return
      }

      const user = loadUserFromStorage()
      const roles = user?.roles ?? []
      const isAdmin = roles.some((r) => ['admin', 'administrator'].includes(String(r).toLowerCase()))
      navigate(isAdmin ? '/admin' : '/dashboard', { replace: true })
    } catch (err: any) {
      const message =
        err?.data?.message ||
        err?.response?.data?.message ||
        err?.message ||
        'Erro ao fazer login. Verifique suas credenciais.'
      setError(message)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background:
            'radial-gradient(900px 600px at 20% 0%, rgba(0,217,255,0.25), transparent 55%),' +
            'radial-gradient(900px 600px at 90% 20%, rgba(255,109,0,0.20), transparent 55%),' +
            'rgba(12, 13, 24, 0.95)',
          border: '1px solid rgba(255,255,255,0.10)',
          backdropFilter: 'blur(12px)'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
              Entrar
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Use sua conta da Warzone Servers para acessar a área do cliente do Palworld.
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" aria-label="fechar">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Email"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            margin="normal"
            required
            autoFocus
          />

          <TextField
            fullWidth
            label="Senha"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            required
          />
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2, gap: 1 }}>
          <Button onClick={handleClose} variant="outlined" disabled={isLoading}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading || !username || !password}>
            {isLoading ? 'Entrando...' : 'Entrar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default LoginDialog
